import { ApolloLink, Observable } from '@apollo/client';
import {
  getActiveSpan,
  spanIsSampled,
  spanToJSON,
  startSpanManual,
} from './sentry/platform';

const TRACEPARENT_VERSION = '00';

// Wraps every Apollo operation in a Sentry child span named `graphql.<op>` and writes a W3C `traceparent` header. Sentry's fetch instrumentation only emits `sentry-trace` + `baggage`, so OTel-instrumented backends (Hasura, pure OTel Django/FastAPI) won't continue our trace without this header.
export const apolloSentryTraceLink = new ApolloLink((operation, forward) => {
  // Skip when no active parent: avoid creating standalone root transactions for background graphql (polling, AppState refresh).
  if (!getActiveSpan()) {
    return forward(operation);
  }

  const operationName = operation.operationName || 'unknown';

  return new Observable((observer) => {
    let subscription: { unsubscribe: () => void } | undefined;
    let finalize: ((status?: 'cancelled') => void) | undefined;

    startSpanManual(
      {
        attributes: { 'graphql.operation': operationName },
        name: `graphql.${operationName}`,
        op: 'graphql.client',
      },
      (span, finish) => {
        const { span_id, trace_id } = spanToJSON(span);
        if (trace_id && span_id) {
          // W3C traceparent flags is an 8-bit bitmask; bit 0 is `sampled`.
          const flags = spanIsSampled(span) ? '01' : '00';
          operation.setContext(
            ({ headers }: { headers?: Record<string, string> }) => ({
              headers: {
                ...headers,
                traceparent: `${TRACEPARENT_VERSION}-${trace_id}-${span_id}-${flags}`,
              },
            }),
          );
        }

        let finalized = false;
        finalize = (status) => {
          if (finalized) {
            return;
          }
          finalized = true;
          if (status === 'cancelled') {
            span.setStatus({ code: 0, message: 'cancelled' });
          }
          finish();
        };

        subscription = forward(operation).subscribe({
          complete: () => {
            finalize?.();
            observer.complete();
          },
          error: (error) => {
            span.setStatus({ code: 2, message: 'internal_error' });
            finalize?.();
            observer.error(error);
          },
          next: (result) => {
            observer.next(result);
          },
        });
      },
    );

    // Component unmount before complete/error — close the span as cancelled so it isn't mislabeled `ok` by the navigation transaction's idle finish.
    return () => {
      finalize?.('cancelled');
      subscription?.unsubscribe();
    };
  });
});
